/**
 * This plugin comes from https://stash.atlassian.com/projects/CONFCLOUD/repos/status-macro/browse
 */
// eslint-disable-next-line no-restricted-imports
import $ from 'jquery';
import window from 'window-or-global';
import MacroBrowser from '../utils/macro-browser';
import { bind } from '@confluence-classic/confluence-event_DO_NOT_USE';
import BookmarkManager from '../resources/bookmark/atlassian-editor-bookmark-manager';
import escapeHtml from '../utils/escape-html';
import { AJS_MAJOR_VERSION } from '../../constants/ajsVersion';

// Stylesheet
// eslint-disable-next-line no-restricted-syntax, @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import './css/status-view.module.css';
// eslint-disable-next-line no-restricted-syntax, @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import './css/status-editor.css';
// 'com.atlassian.auiplugin:aui-experimental-lozenge' was a dependency of this plugin but it should already be available.

const EDIT_MACRO_BUTTON_EDIT_CLASS = 'macro-placeholder-property-panel-edit-button';
// css class for both label and input representations of the title
const INPUT_ELEMENT_CLASSES = 'status-macro-title first last editable';
const ENTER_KEY = 13;
const ESCAPE_KEY = 27;

const STATUS_MACRO_NAME = 'status';
// property-panel button ids from the plugin descriptor, assumes that the parameter names equal the button ids

const STATUS_MACRO_COLORS =
	AJS_MAJOR_VERSION > 6
		? ['Grey', 'Green', 'Red', 'Blue', 'Purple', 'Yellow']
		: ['Grey', 'Red', 'Yellow', 'Green', 'Blue'];
const STATUS_MACRO_COLOR_DEFAULT = STATUS_MACRO_COLORS[0];

let deferredUpdate;
let macroNode, macroParameters, $inputTitleElement;

/**
 * Check if the macro has been updated
 * @param newParameters
 * @returns {boolean}
 */
function hasMacroChanged() {
	updateParameters();

	const originalMacroParameters = window.Confluence.MacroParameterSerializer.deserialize(
		$(macroNode).attr('data-macro-parameters'),
	);
	let hasTitleUpdated = false;
	let hasColourUpdated = false;

	if (originalMacroParameters && macroParameters) {
		hasTitleUpdated = originalMacroParameters.title !== macroParameters.title;
		hasColourUpdated = originalMacroParameters.colour !== macroParameters.colour;
	}

	return hasTitleUpdated || hasColourUpdated;
}

/**
 * Binds macroParameters with values from the UI
 */
function updateParameters() {
	if ($inputTitleElement) {
		const isDefaultMacroTitle = getMacroColor(macroParameters) == $inputTitleElement.val();
		const isEmpty = $inputTitleElement.val() === '';
		if (isDefaultMacroTitle || isEmpty) {
			delete macroParameters.title;
		} else {
			macroParameters.title = $inputTitleElement.val();
		}
	}
}

/**
 * Calls the server to get the updated node and update the DOM asynchronously.
 */
function updateMacro() {
	if (deferredUpdate && deferredUpdate.state && deferredUpdate.state() === 'pending') {
		if (process.env.NODE_ENV !== 'production') {
			console.log('Macro update already in progress.');
		}
		return; // request not completed
	}

	updateParameters();

	const macroRenderRequestParameters = {
		contentId: window.Confluence.Editor.getContentId(),
		macro: {
			name: STATUS_MACRO_NAME,
			params: macroParameters,
			defaultParameterValue: '',
			body: '',
		},
	};

	// onGoingRequest is a jQuery deferred object to be fulfilled when the operation is completed
	deferredUpdate = window.tinymce.confluence.MacroUtils.insertMacro(
		macroRenderRequestParameters,
		macroNode,
	);

	function selectNodeAndBookmark(macroNode) {
		window.tinymce.activeEditor.selection.select(macroNode);
		BookmarkManager.storeBookmark();
		window.tinymce.activeEditor.selection.collapse();
	}

	deferredUpdate
		.done((newNode, newNodeMarkup) => {
			newNodeMarkup; // Just to appease linter.
			macroNode = newNode;
			selectNodeAndBookmark(macroNode);
		})
		.fail((error) => {
			if (process.env.NODE_ENV !== 'production') {
				console.error(`Failed to load status macro - ${error}`);
			}
		});
}

/**
 * Retrieves the current color from the parameters or returns the default.
 * @param {object} macroParameters whose color and colour property will be read.
 * @returns {string} a color from STATUS_MACRO_COLORS such as "Red"
 */
function getMacroColor(macroParameters) {
	return macroParameters.colour || macroParameters.color || STATUS_MACRO_COLOR_DEFAULT;
}

function cancelEdit() {
	$inputTitleElement = null;
	return true;
}

/**
 * Builds the actual input box element
 */
function makeTitleInputElement(macroParameters) {
	macroParameters; // Just to appease linter.
	const $inputTitleElement = $('<input/>');
	$inputTitleElement.attr('class', INPUT_ELEMENT_CLASSES);

	$inputTitleElement.keyup((e) => {
		if (e.keyCode === ENTER_KEY) {
			window.AJS.Confluence.PropertyPanel.destroy();
			// if its an enter key we want to prevent the default action since
			// we want the cursor right after the macro.
			return false;
		}
		return true;
	});

	$inputTitleElement.keydown((e) => {
		if (e.keyCode === ESCAPE_KEY) {
			return cancelEdit();
		}
		return true;
	});

	return $inputTitleElement;
}

/**
 * Replaces the title element with an editable input text with event handlers.
 */
function clickTitleInputElement($titleButtonElement) {
	$inputTitleElement = makeTitleInputElement(macroParameters);
	$inputTitleElement.val($titleButtonElement.find('span.panel-button-text').text());
	$titleButtonElement.replaceWith($inputTitleElement);
	$inputTitleElement.select().focus();
}

/**
 * Register event handlers for click events on color buttons
 */
function registerColorButtonsEventHandlers() {
	$.each(STATUS_MACRO_COLORS, (idx, colour) => {
		const onColorButtonClick = function (e, macroNode) {
			macroNode; // Just to appease linter.
			macroParameters.colour = colour;
			window.AJS.Confluence.PropertyPanel.destroy();
		};

		window.AJS.Confluence.PropertyPanel.Macro.registerButtonHandler(
			colour,
			onColorButtonClick,
			STATUS_MACRO_NAME,
		);
	});
}

function getInitialTitle(macroParameters) {
	return macroParameters.title || getMacroColor(macroParameters);
}

/**
 * Creates the label/button title element. When clicked, it gets converted in an input text box.
 * @param currentTitle
 * @returns {{className: string, text: *, click: Function}}
 */
function createTitleButtonElement(currentTitle) {
	return {
		className: INPUT_ELEMENT_CLASSES,
		text: escapeHtml(currentTitle),
		click: clickTitleInputElement,
	};
}

/**
 * Gets the edit button among the collection of available buttons, checking its CSS class (not ideal)
 * @param buttons
 * @returns {*}
 */
function getEditButton(buttons) {
	for (let i = 0; i < buttons.length; i++) {
		if (buttons[i].className.indexOf(EDIT_MACRO_BUTTON_EDIT_CLASS) > -1) {
			return buttons[i];
		}
	}
	return null;
}

/**
 * Override the edit button to wait for the updateMacro's promise to be resolved before opening the macrobrowser
 * dialog.
 *
 * @param buttons
 */
function overrideEditButton(buttons) {
	const editButton = getEditButton(buttons);

	const editClickAction = function (node) {
		MacroBrowser.editMacro($(node));
	};

	editButton.click = function () {
		window.AJS.Confluence.PropertyPanel.destroy();

		if (!hasMacroChanged()) {
			editClickAction(macroNode);
		} else {
			// deferredUpdate is populated by calling destroy() if the macro has changed, since destroy() calls updateMacro, which
			// sets deferredUpdate.
			// This should probably be less confusing...
			deferredUpdate.always(editClickAction);
		}
	};
}

export function statusMacro() {
	window.AJS.Confluence.PropertyPanel.Macro.registerInitHandler(
		(initMacroNode, buttons, options) => {
			options; // Just to appease linter.
			macroNode = initMacroNode;
			overrideEditButton(buttons);
			macroParameters = window.Confluence.MacroParameterSerializer.deserialize(
				$(macroNode).attr('data-macro-parameters'),
			);
			$inputTitleElement = null;

			// insert title button after the first element = 'Edit'
			buttons.splice(1, 0, createTitleButtonElement(getInitialTitle(macroParameters)));
		},
		'status',
	);

	registerColorButtonsEventHandlers();

	bind('destroyed.property-panel', () => {
		if (hasMacroChanged()) {
			updateMacro();
		}
	});
}

export function statusMacroWRM() {
	// This web-resource has already been requested by property-panel-image-link-macro. This is duplicity but should be handled by WRM as such.
	// It provides a way to bind to this WRM promise.
	// The web-resource contains property-panel-macros.js that defines AJS.Confluence.PropertyPanel.Macro.
	return ['wr!com.atlassian.confluence.editor:property-panel-image-link-macro-conf-frontend'];
}
